import React, { useState } from "react";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { Box, Icon, useBreakpointValue } from "@chakra-ui/react";
import { MdPerson, MdMusicNote, MdEmail, MdPayment, MdEvent, MdReceipt } from "react-icons/md";
import ContactInfoRow from "./shared/ContactInfoRow";
import getStatusColor from "./shared/getStatusColor";

/**
 * Renders the invoice and reference rows at the top of contact details.
 * @param {Object} props
 * @param {Object} props.contactCard - The contact card data object
 * @param {boolean} props.invoiceCopied - Whether the invoice was just copied
 * @param {Function} props.onCopyInvoice - Handler to copy the invoice
 * @param {string} props.refIndexCopied - Currently copied reference field id
 * @param {Function} props.onCopy - Handler to copy a reference field
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function ContactHeaderRows({ contactCard, invoiceCopied, onCopyInvoice, refIndexCopied, onCopy, isMobile }) {
    return (
        <>
            {contactCard?.payment?.invoice && (
                <ContactInfoRow
                    icon={<Icon as={MdReceipt} />}
                    label="Facture"
                    value={contactCard.payment.invoice.substring(0, 10) + "..."}
                    isInvoice invoiceUrl={contactCard.payment.invoice}
                    copyable isCopied={invoiceCopied}
                    onCopy={onCopyInvoice} isMobile={isMobile}
                />
            )}
            {contactCard?.payment?.reference && (
                <ContactInfoRow
                    icon={<Icon as={MdPayment} />}
                    label="Reference"
                    value={contactCard.payment.reference.substring(0, 9) + "****"}
                    copyable isCopied={refIndexCopied === `ref-${contactCard.id}`}
                    onCopy={() => onCopy(`ref-${contactCard.id}`, contactCard.payment.reference)}
                    isMobile={isMobile}
                />
            )}
        </>
    );
}

/**
 * Renders the person, email, and artist detail rows.
 * @param {Object} props
 * @param {Object} props.contactCard - The contact card data object
 * @param {string} props.refIndexCopied - Currently copied reference field id
 * @param {Function} props.onCopy - Handler to copy a field
 * @param {string} props.currency - Currency symbol
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function ContactBodyRows({ contactCard, refIndexCopied, onCopy, currency, isMobile }) {
    return (
        <>
            <ContactInfoRow
                icon={<Icon as={MdPerson} />}
                label="L'Auditeur Pro"
                value={`${contactCard?.reservation?.auditor_name} (Pour ${contactCard?.name} ${contactCard?.lastname || ''})`}
                isMobile={isMobile}
            />
            {contactCard?.payment?.email && (
                <ContactInfoRow
                    icon={<Icon as={MdEmail} />}
                    label="Email (de l'auditeur pro)"
                    value={contactCard.payment.email}
                    copyable isCopied={refIndexCopied === `email-${contactCard.id}`}
                    onCopy={() => onCopy(`email-${contactCard.id}`, contactCard.payment.email)}
                    isMobile={isMobile}
                />
            )}
            <ContactInfoRow
                icon={<Icon as={MdMusicNote} />}
                label="Artiste" value={contactCard?.reservation?.artist_name}
                isMobile={isMobile}
            />
            <ContactInfoRow
                icon={<Icon as={MdPayment} />}
                label="Frais de contact"
                value={contactCard?.amount ? `${contactCard.amount}${currency}` : '*Offert'}
                isMobile={isMobile}
            />
        </>
    );
}

/**
 * Renders the status and date footer rows.
 * @param {Object} props
 * @param {Object} props.contactCard - The contact card data object
 * @param {Object} props.statusColors - Color scheme for the status chip
 * @param {string} props.statusLabel - Translated status label
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function ContactFooterRows({ contactCard, statusColors, statusLabel, isMobile }) {
    return (
        <>
            <ContactInfoRow
                label="Statut" value={statusLabel}
                chip chipColor={statusColors} isMobile={isMobile}
            />
            <ContactInfoRow
                icon={<Icon as={MdEvent} />}
                label="Envoyee le"
                value={dayjs(contactCard?.created_at).format("DD-MM-YYYY a HH:mm")}
                isMobile={isMobile}
            />
            <ContactInfoRow
                icon={<Icon as={MdEvent} />}
                label="Derniere modification"
                value={dayjs(contactCard?.modified_at).format("DD-MM-YYYY a HH:mm")}
                isMobile={isMobile}
            />
        </>
    );
}

/**
 * Displays detailed information for a contact card including invoice,
 * reference, parties involved, payment, status, and dates.
 * @param {Object} props
 * @param {Object} props.contactCard - The contact card data object
 * @param {string} props.currency - Currency symbol to display
 * @returns {React.ReactElement}
 */
function ContactDetails({ contactCard, currency }) {
    const { status } = useSelector((state) => state.global);
    const [refIndexCopied, setRefIndexCopied] = useState(null);
    const [invoiceCopied, setInvoiceCopied] = useState(false);
    const isMobile = useBreakpointValue({ base: true, sm: false });
    const statusColors = getStatusColor(contactCard?.status);

    /**
     * Copies text to clipboard and tracks the copied item by id.
     * @param {string} id - Unique identifier for the copied field
     * @param {string} text - Text to copy to clipboard
     * @returns {void}
     */
    const handleCopy = (id, text) => {
        navigator.clipboard.writeText(text);
        setRefIndexCopied(id);
        setTimeout(() => setRefIndexCopied(null), 2000);
    };

    /**
     * Copies the invoice URL to clipboard and shows feedback.
     * @returns {void}
     */
    const handleCopyInvoice = () => {
        if (!contactCard?.payment?.invoice) return;
        navigator.clipboard.writeText(contactCard.payment.invoice);
        setInvoiceCopied(true);
        setTimeout(() => setInvoiceCopied(false), 2000);
    };

    return (
        <Box>
            <ContactHeaderRows
                contactCard={contactCard} invoiceCopied={invoiceCopied}
                onCopyInvoice={handleCopyInvoice} refIndexCopied={refIndexCopied}
                onCopy={handleCopy} isMobile={isMobile}
            />
            <ContactBodyRows
                contactCard={contactCard} refIndexCopied={refIndexCopied}
                onCopy={handleCopy} currency={currency} isMobile={isMobile}
            />
            <ContactFooterRows
                contactCard={contactCard} statusColors={statusColors}
                statusLabel={status[contactCard?.status]} isMobile={isMobile}
            />
        </Box>
    );
}

export default ContactDetails;
